import { type Puck, type Report } from '@psyomics/ui-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UseQueryExecute } from 'urql';

import { ReportData, GetReportDataQuery, Survey, ReferralSession } from './generated/report';
import { useReportPdfUrlQuery } from './hooks/use-report-pdf-url';
import { ErrorType, useReportQuery } from './hooks/use-report-query';

type ReportProps = {
  readonly referralId: string;
  readonly referralSessionId?: string;
  readonly path?: string;
  readonly navigateToPath: (path: string) => void;
};

const useSurveyDefinition = (reportData: ReportData | null | undefined) => {
  const surveys = useMemo(
    () =>
      (reportData?.surveys ?? []).map((survey) => ({
        ...survey,
        definition: JSON.parse(survey.definition),
      })),
    [reportData]
  );
  return surveys;
};

const mapAssociatedReports = (reports?: ReferralSession[]): Report[] => {
  if (!reports) return [];
  return reports.map<Report>((report) => ({
    completedAt: report.completedAt ?? undefined,
    referralId: report.referral.id,
    referralSessionId: report.id,
    service: report.referral.service.description,
    status: report.status,
    updatedAt: report.updatedAt,
  }));
};

const useReportQueries = (referralId: string, referralSessionId?: string) => {
  const { data, fetching, errorMessage } = useReportQuery({
    referralId,
    referralSessionId,
  });

  const { executeQuery: executeFetchReportPdfUrlQuery, data: reportPdfUrlData } = useReportPdfUrlQuery({
    referralSessionId,
  });

  useEffect(() => {
    if (reportPdfUrlData?.url) window.open(reportPdfUrlData.url, '_blank');
  }, [reportPdfUrlData]);

  const fetchReportPdfUrl = useCallback(() => {
    if (referralSessionId) executeFetchReportPdfUrlQuery();
  }, [referralSessionId, executeFetchReportPdfUrlQuery]);

  return {
    associatedReports: data?.getReportData?.associatedReports,
    errorMessage,
    fetchReportPdfUrl,
    fetching,
    reportData: data?.getReportData?.reportData,
  };
};

type UseFetchReportDataResponse = {
  associatedReports?: GetReportDataQuery['getReportData']['associatedReports'];
  errorMessage?: ErrorType;
  fetchReportPdfUrl: UseQueryExecute;
  fetching: boolean;
  reportKey: string;
  parsedAnswers: Puck.AnswersByRespondentType;
  reportData?: NonNullable<GetReportDataQuery['getReportData']['reportData']>;
  definition: Puck.Data;
  surveys: Survey[];
  reports: Report[];
};
const useFetchReportData = ({
  referralId,
  referralSessionId,
}: Pick<ReportProps, 'referralId' | 'referralSessionId'>): UseFetchReportDataResponse => {
  const navigate = useNavigate();
  const [searchedForSession, setSearchedForSession] = useState<boolean>(Boolean(referralSessionId));
  const { reportData, associatedReports, fetchReportPdfUrl, fetching, errorMessage } = useReportQueries(
    referralId,
    referralSessionId
  );

  useEffect(() => {
    if (!referralSessionId && (associatedReports || errorMessage)) {
      if (associatedReports) {
        const foundReferralSessionId = associatedReports.filter((r) => r.referral.id === referralId).at(-1)?.id;

        if (foundReferralSessionId) {
          navigate(`./${foundReferralSessionId}`);
          return;
        }
      }

      setSearchedForSession(true);
    }
  }, [referralSessionId, associatedReports, errorMessage, navigate, referralId]);

  const parsedAnswers = useMemo(() => JSON.parse(reportData?.answers ?? '{}'), [reportData]);
  const surveys = useSurveyDefinition(reportData);
  const definition = useMemo(
    () => (reportData?.reportShapeDefinition ? JSON.parse(reportData.reportShapeDefinition).definition : undefined),
    [reportData?.reportShapeDefinition]
  );
  const reportKey = useMemo(() => surveys.map((s) => s.id).join() + referralSessionId, [referralSessionId, surveys]);
  const reports = useMemo(() => mapAssociatedReports(associatedReports), [associatedReports]);

  const isResolvingSession = !referralSessionId && !searchedForSession;
  const isFetching = fetching || isResolvingSession;

  return {
    associatedReports,
    definition,
    errorMessage,
    fetchReportPdfUrl,
    fetching: isFetching,
    parsedAnswers,
    reportData: reportData ?? undefined,
    reportKey,
    reports,
    surveys,
  };
};

export { useFetchReportData, type UseFetchReportDataResponse };
